import { useCrmLocale } from '../../ui/locale/crmLocale';
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/useAuth";
import {
  getAccountSettings,
  updateAccountPreferences,
  type AccountPreferences,
  type AccountSettings,
} from "../../api/accountSettings";
import { QUERY_META } from "../../queries/core/queryMeta";
import {
  DEFAULT_ACCOUNT_PREFERENCES,
  publishAccountLanguage,
} from "./preferenceEvents";
import "./staff-display.css";
import { useToast } from "../../ui/hooks";

import { Context } from './useAccountPreferences';
function PrincipalPreferences({ children }: { children: React.ReactNode }) {
  const { t: crmText } = useCrmLocale();
  const { user, authEpoch } = useAuth();
  const client = useQueryClient();
  const toast = useToast();
  const mounted = React.useRef(false);
  React.useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const staff = Boolean(user?.id && user.role && user.role !== "student");
  const key = React.useMemo(
    () =>
      [
        "employee",
        "account-settings",
        user?.id,
        user?.role,
        authEpoch,
        user?.simulation?.target_employee_id ?? null,
      ] as const,
    [user?.id, user?.role, authEpoch, user?.simulation?.target_employee_id],
  );
  const query = useQuery({
    queryKey: key,
    queryFn: getAccountSettings,
    enabled: staff,
    staleTime: 60_000,
    retry: 1,
    meta: QUERY_META.sensitive,
  });
  const preferences = query.data?.preferences ?? DEFAULT_ACCOUNT_PREFERENCES;
  const mutation = useMutation({
    mutationFn: updateAccountPreferences,
    meta: QUERY_META.sensitive,
  });
  const savePreferences = React.useCallback(
    async (patch: Partial<AccountPreferences>) => {
      const result = await mutation.mutateAsync(patch);
      if (!mounted.current) return;
      client.setQueryData<AccountSettings>(key, (previous) =>
        previous ? { ...previous, preferences: result.preferences } : previous,
      );
    },
    [client, key, mutation],
  );
  const updateProfile = React.useCallback(
    (profile: AccountSettings["profile"]) => {
      client.setQueryData<AccountSettings>(key, (previous) =>
        previous ? { ...previous, profile } : previous,
      );
    },
    [client, key],
  );
  React.useLayoutEffect(() => {
    if (!staff) return;
    document.body.dataset.staffTextSize = preferences.text_size;
    document.body.dataset.staffDensity = preferences.density;
    publishAccountLanguage(preferences.language);
    return () => {
      delete document.body.dataset.staffTextSize;
      delete document.body.dataset.staffDensity;
      publishAccountLanguage(null);
    };
  }, [staff, preferences.text_size, preferences.density, preferences.language]);
  const saveLanguageRef = React.useRef(savePreferences);
  saveLanguageRef.current = savePreferences;
  const currentLanguageRef = React.useRef(preferences.language);
  currentLanguageRef.current = preferences.language;
  const languageQueue = React.useRef<{
    requested: "en" | "ar" | null;
    pending: boolean;
  }>({ requested: null, pending: false });
  React.useEffect(() => {
    if (!staff || !query.data?.capabilities.can_edit) return;
    const changed = (event: Event) => {
      const language = (event as CustomEvent).detail;
      if (language !== "en" && language !== "ar") return;
      const queue = languageQueue.current;
      queue.requested = language;
      if (queue.pending) return;
      queue.pending = true;
      void (async () => {
        try {
          while (
            mounted.current &&
            queue.requested !== currentLanguageRef.current
          ) {
            const requested = queue.requested!;
            await saveLanguageRef.current({ language: requested });
            if (!mounted.current) return;
            currentLanguageRef.current = requested;
          }
        } catch {
          if (!mounted.current) return;
          publishAccountLanguage(currentLanguageRef.current);
          toast.push?.(crmText("Unable to save language preference"), "danger");
        } finally {
          queue.pending = false;
        }
      })();
    };
    window.addEventListener("arabian:staff-language-choice", changed);
    return () =>
      window.removeEventListener("arabian:staff-language-choice", changed);
  }, [staff, query.data?.capabilities.can_edit, toast, crmText]);
  return (
    <Context.Provider
      value={{
        settings: query.data,
        preferences,
        loading: staff && query.isPending,
        error: query.error,
        saving: mutation.isPending,
        savePreferences,
        updateProfile,
        refetch: () => {
          void query.refetch();
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function AccountPreferencesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, authEpoch } = useAuth();
  return (
    <PrincipalPreferences
      key={`${user?.id}:${user?.role}:${authEpoch}:${user?.simulation?.active}:${user?.simulation?.target_employee_id}`}
    >
      {children}
    </PrincipalPreferences>
  );
}
